import React, { useMemo, useState } from "react";
import { observer } from "mobx-react";
import { MoleculeCell } from "./sar/MoleculeCell";
import { PotencyCell } from "./sar/PotencyCell";
import { HighlightChip } from "./sar/HighlightChip";
import { sarSpecToCsv } from "./sar/sarSpecToCsv";
import { useArtifactSourceRegistry } from "../../pages/workspace/panels/artifactProvenance";
import { SourceColChip } from "./SourceColChip";

// Row-header superscript [n] that links to the unified source registry.
// `n` is the store-side ordinal so it matches the same numeric the
// Sources panel uses in its list ("[1]", "[2]"…).
const SourceRowRef = ({ storeId, onClick }) => {
  if (!storeId) return null;
  const n = String(storeId).replace(/^source-/, "");
  return (
    <button
      type="button"
      className="sar-src-ref"
      onClick={(e) => { e.stopPropagation(); onClick?.(storeId); }}
      title="View source in panel"
      data-testid={`sar-src-ref-${storeId}`}
    >
      [{n}]
    </button>
  );
};

// Subscript digits for R-group header labels (R₁, R₂, …)
const SUBSCRIPT = "₁₂₃₄₅₆₇₈₉";

function DataCell({ value, column }) {
  if (value === null || value === undefined) {
    return <span className="data-cell--na">—</span>;
  }
  switch (column.format) {
    case "potency":
      return (
        <PotencyCell
          value={value}
          lowerBetter={column.lower_better !== false}
          breakpoints={column.ramp_breakpoints}
        />
      );
    case "ratio": {
      const high = value >= 10;
      return (
        <span className={`ratio-cell${high ? " ratio-cell--high" : ""}`}>
          {value}×
        </span>
      );
    }
    case "numeric": {
      const p = Number.isInteger(column.precision) ? column.precision : 1;
      return <span className="numeric-cell">{Number(value).toFixed(p)}</span>;
    }
    case "text":
    default:
      return (
        <span className="text-cell" title={String(value)}>
          {String(value)}
        </span>
      );
  }
}

export const SARTableHost = observer(function SARTableHost({ spec, sourceReferencesStore }) {
  const rGroupKeys = spec.r_groups || [];
  const columns = spec.columns || [];

  // Register the spec's sources[] into the chat-wide Sources panel so
  // chip / superscript clicks highlight the canonical row there.
  // Backward compatible: specs without `sources` produce an empty map
  // and the affordances stay silent.
  const { bySpecId, rowSourceId, columnSourceId, storeIdFor, resolveStoreId } = useArtifactSourceRegistry(
    spec,
    sourceReferencesStore,
    { turnId: `sar-${spec.chart_id || spec.id || "anon"}` },
  );

  // `src` (from SourceColChip) lets us resolve on demand when the chip's
  // render-time storeId is null (unregistered mid-stream / plexId-only).
  const handleSourceClick = (storeId, src) => {
    const id = storeId || (src?.id && resolveStoreId ? resolveStoreId(src.id, { synthesize: true }) : null);
    if (!id || !sourceReferencesStore) return;
    if (typeof sourceReferencesStore.scrollToSource === "function") {
      sourceReferencesStore.scrollToSource(id);
    } else if (typeof sourceReferencesStore.highlight === "function") {
      sourceReferencesStore.highlight(id);
    }
  };

  // Sort state: { key: string|null, direction: "none"|"asc"|"desc" }
  // Clicking a column header cycles: none → asc → desc → none.
  const [sort, setSort] = useState({ key: null, direction: "none" });

  // Diff-mode: when true, R-group MoleculeCells receive diffMode=true.
  // Full-molecule rows are unaffected.
  const [diffMode, setDiffMode] = useState(false);

  const handleHeaderClick = (column) => {
    setSort((cur) => {
      if (cur.key !== column.key) return { key: column.key, direction: "asc" };
      if (cur.direction === "asc") return { key: column.key, direction: "desc" };
      if (cur.direction === "desc") return { key: null, direction: "none" };
      return { key: column.key, direction: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    const rows = [...(spec.rows || [])];
    if (sort.key === null || sort.direction === "none") return rows;
    rows.sort((a, b) => {
      const va = a.data?.[sort.key];
      const vb = b.data?.[sort.key];
      if (va === undefined || va === null) return 1;
      if (vb === undefined || vb === null) return -1;
      const cmp = (typeof va === "number" && typeof vb === "number")
        ? (va - vb)
        : String(va).localeCompare(String(vb));
      return sort.direction === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [spec.rows, sort]);

  const handleRowClick = (row, idx) => {
    const specId = rowSourceId(row, idx);
    // Resolve on demand (register if needed) rather than only reading the
    // render-time map — a click mid-stream still lands on the Sources row.
    const storeId = (resolveStoreId && resolveStoreId(specId, { synthesize: true })) || storeIdFor(specId);
    if (storeId) {
      handleSourceClick(storeId);
      return;
    }
    // Legacy fallback: row.source_id may already be a store id when
    // the spec didn't carry a top-level sources[] but the row was
    // pre-attributed by the search pipeline.
    if (row.source_id && sourceReferencesStore?.highlight) {
      sourceReferencesStore.highlight(row.source_id);
    }
  };

  const sortIndicator = (col) => {
    if (sort.key !== col.key || sort.direction === "none") return null;
    return sort.direction === "asc" ? " ↑" : " ↓";
  };

  // Task 22 — CSV download handler
  const handleDownloadCsv = () => {
    const { csv, filename } = sarSpecToCsv(spec);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="sar-host">
      {/* Core scaffold band + toolbar (toolbar always visible) */}
      <div className="sar-host__core">
        <span className="sar-host__core-label">Core</span>
        {spec.core && <MoleculeCell mol={spec.core} size="core" />}
        <span className="sar-host__core-name">{spec.core?.label || ""}</span>
        <div className="sar-host__toolbar">
          <button
            type="button"
            onClick={() => setDiffMode((v) => !v)}
            aria-pressed={diffMode}
            title="Show R-group fragments only"
          >
            R-group diff
          </button>
          <button type="button" onClick={handleDownloadCsv} title="Download CSV">
            Download CSV
          </button>
        </div>
      </div>

      <table className="sar-table">
        {/* Explicit column widths — table-layout: fixed honors these. Without them the
            equal-share default makes R-group cells overflow into adjacent columns and
            squeezes data headers. R-group columns match the 80px molecule cell width. */}
        <colgroup>
          <col className="sar-col--idx" />
          <col className="sar-col--cmpd" />
          {rGroupKeys.map((k) => (
            <col key={k} className="sar-col--rg" />
          ))}
          {columns.map((c) => (
            <col
              key={c.key}
              className={`sar-col--${c.format || "text"}`}
            />
          ))}
          <col className="sar-col--src" />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>cmpd</th>
            {rGroupKeys.map((k, i) => (
              <th key={k}>{`R${SUBSCRIPT.charAt(i) || (i + 1)}`}</th>
            ))}
            {columns.map((c) => {
              const specId = columnSourceId(c, c.key);
              const src = specId ? bySpecId.get(specId) : null;
              const storeId = storeIdFor(specId);
              return (
                <th
                  key={c.key}
                  className="sar-th--sortable"
                  onClick={() => handleHeaderClick(c)}
                  role="columnheader"
                  aria-sort={
                    sort.key === c.key
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {c.label}
                  {sortIndicator(c)}
                  {src && (
                    <SourceColChip src={src} storeId={storeId} onClick={handleSourceClick} />
                  )}
                </th>
              );
            })}
            <th>src</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, idx) => {
            const rowSpecId = rowSourceId(row, idx);
            const rowStoreId = storeIdFor(rowSpecId);
            const clickable = !!rowStoreId || !!row.source_id;
            return (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row, idx)}
                className={clickable ? "sar-row sar-row--clickable" : "sar-row"}
              >
                <td>{idx + 1}</td>
                <td>
                  {row.label}
                  {row.highlight && <HighlightChip value={row.highlight} />}
                </td>
                {row.r_groups
                  ? rGroupKeys.map((k) => (
                      <td key={k}>
                        <MoleculeCell
                          mol={row.r_groups[k] || {}}
                          size="xs"
                          diffMode={diffMode}
                        />
                      </td>
                    ))
                  : (
                    <td colSpan={rGroupKeys.length || 1}>
                      {/* Full-molecule rows do NOT get diffMode */}
                      <MoleculeCell mol={row.molecule || {}} size="small" />
                    </td>
                  )}
                {columns.map((c) => (
                  <td key={c.key}>
                    <DataCell value={row.data?.[c.key]} column={c} />
                  </td>
                ))}
                <td>
                  {rowStoreId
                    ? <SourceRowRef storeId={rowStoreId} onClick={handleSourceClick} />
                    : (row.source_id ? `[${row.source_id}]` : "")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});
